/**
 * Collection store — manages collection metadata AND per-collection block
 * indices in one place: a single store handles both the "list of
 * collections" surface and "blocks in this collection".
 *
 * Reads (metadata):
 *   collectionStore.useList()             — Personal Collections (layer 1):
 *                                           Primary-rooted + standalone state
 *   collectionStore.useListAllWritable()  — Every writable layer-1 Collection
 *                                           across all three states, enriched
 *                                           with parent Group title for
 *                                           Group-rooted entries
 *   collectionStore.useListGroups()       — Non-Primary Groups (layer 0)
 *   collectionStore.useFindById()         — cross-layer lookup by id
 *
 * Reads (per-collection blocks — pointer rows joined to `blockStore`):
 *   collectionStore.useBlocks(scopeId, options?) — { rows: [{blockId, sortKey}],
 *                                                    hasMore, fetchNextPage,
 *                                                    isLoading, isFetchingNextPage }
 *
 * Writes (metadata):
 *   collectionStore.create({ title })            — optimistic insert + server create
 *   collectionStore.createGroup({ title })       — create a layer-0 Group
 *   collectionStore.createStack(parent, { ... }) — create a layer-2 Stack
 *   collectionStore.updateTitle(id, title)       — optimistic rename + server update
 *   collectionStore.detachFromPrimary(id)        — pull a Collection out
 *                                                  of Primary into the
 *                                                  standalone state so
 *                                                  it can have its own
 *                                                  members (share-without-Group)
 *   collectionStore.copyBlocks(id, blockIds)     — copy blocks into a collection
 *
 * Writes (per-collection blocks):
 *   collectionStore.loadBlocks(scopeId, options?)        — fetch+cache a page
 *   collectionStore.loadBlocksByIds(scopeId, ids)        — for smart collections
 *   collectionStore.refreshScopeTopPages()               — re-fetch the top page of every loaded scope (return-from-background)
 *   collectionStore.addBlock(scopeId, blockId, sortKey)  — optimistic membership add
 *   collectionStore.removeBlock(scopeId, blockId)        — optimistic membership remove
 *   collectionStore.removeBlockEverywhere(blockId)       — wipe across all scopes (delete)
 *   collectionStore.removeAllBlocksFromScope(scopeId)    — clear a scope (smart-collection cleanup)
 *   collectionStore.bumpBlockSortKey(blockId, sortKey)   — update sortKey across all scopes (surface)
 *   collectionStore.invalidateBlocks(scopeId)            — drop scope state + rows so next mount refetches
 */
import { useEffect, useMemo, useState } from "react";
import { useLiveQuery, eq } from "@tanstack/react-db";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { generateId } from "@shared/types/id";
import { ConfiguredLogger } from "@/lib/logger";
import { fetchBlockEnvelope, getCollectionDetails, listCollections, } from "@/api/query-api";
import { createCollection as apiCreateCollection, createChildCollection as apiCreateChildCollection, createGroup as apiCreateGroup, createStack as apiCreateStack, deleteCollection as apiDeleteCollection, detachCollectionFromPrimary as apiDetachCollectionFromPrimary, moveBlocksBetweenParentChild as apiMoveBlocksBetweenParentChild, copyBlocksToCollection as apiCopyBlocks, setCollectionParent as apiSetCollectionParent, updateCollection as apiUpdateCollection, } from "@/api/command-api";
import { blockStore } from "@/data/block-store";
import { queryClient } from "@/data/query-client";
import { collectionCollection } from "./collection-collection";
import { collectionBlocksCollection, collectionBlockRowKey, } from "./collection-blocks-collection";
const logger = new ConfiguredLogger({ caller: "CollectionStore" });
// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------
async function refreshCollections() {
    try {
        // Pull every layer so the cross-layer cache stays complete — both
        // the Collections tab (layer 1) and the Stack-parent / nav-tab
        // lookups (cross-layer) read from this same store. Per-surface
        // filtering happens in the consumer.
        const data = await listCollections();
        collectionCollection.utils.writeBatch(() => {
            for (const col of data.collections) {
                collectionCollection.utils.writeUpsert(col);
            }
        });
    }
    catch (e) {
        logger.warn("Failed to refresh collections", { error: e instanceof Error ? e.message : String(e) });
    }
}
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
/**
 * The "Collections" surface — layer-1 Collections only. Groups (layer 0)
 * have their own switcher and Stacks (layer 2) live inside their parent
 * Collection's view, so neither belongs in the cross-cutting list of
 * destinations the user manipulates from the Collections tab,
 * add-to-collection popover, etc.
 *
 * Backed by the cross-layer `collectionCollection` cache; we filter
 * here in the hook so id-based lookups (e.g. Stack-parent resolution)
 * can still consult the underlying cache directly.
 *
 * Layer-1 Collections live in one of three states under the
 * standalone-Collections model (2026-05-01):
 *   1. Primary-rooted (`parentCollectionId === primary.id`) — private.
 *   2. Standalone (`parentCollectionId === undefined`) — owns its own ACL.
 *   3. Group-rooted (`parentCollectionId === some Group`) — inherits ACL.
 *
 * The Collections tab surfaces (1) and (2): the user's "personal"
 * collections, whether private under Primary or directly shared with a
 * handful of people via standalone state. Group-rooted (3) belongs to
 * the Group's view, not the cross-cutting surface, so it's filtered
 * out.
 */
function useList() {
    const { data, isLoading } = useLiveQuery((q) => q.from({ c: collectionCollection }), []);
    const raw = (data ?? []);
    const primaryId = useMemo(() => {
        const primary = raw.find((c) => c.type === "primary");
        return primary?.id;
    }, [raw]);
    // Order by recency of most recently added block (`lastAddedAt`), falling
    // back to `createdAt` for empty collections. Mirrors the server-side
    // ordering in `CollectionRepository.listByAccountIdWithCounts`.
    // Entries without an explicit `layerIndex` (older API responses,
    // optimistic stubs) default to `COLLECTION_LAYER.collection` so they
    // surface here rather than silently disappearing.
    const collections = useMemo(() => {
        const personalCollections = raw.filter((c) => {
            const layer = c.layerIndex ?? COLLECTION_LAYER.collection;
            if (layer !== COLLECTION_LAYER.collection)
                return false;
            // Standalone (parentless layer-1) is a first-class state under
            // the standalone-Collections model — surface it here.
            if (c.parentCollectionId === undefined)
                return true;
            // Primary-rooted: surface (private to this user).
            return c.parentCollectionId === primaryId;
        });
        return [...personalCollections].sort((a, b) => {
            const aKey = a.lastAddedAt ?? a.createdAt;
            const bKey = b.lastAddedAt ?? b.createdAt;
            if (aKey === bKey)
                return b.createdAt.localeCompare(a.createdAt);
            return bKey.localeCompare(aKey);
        });
    }, [raw, primaryId]);
    return { collections, isLoading };
}
/**
 * Every layer-1 Collection the user can write to, across all three
 * states (Primary-rooted, standalone, Group-rooted). Powers
 * cross-cutting destination pickers (e.g. the multiselect "Add to
 * collection" popover) that should be able to target any Collection
 * regardless of which workspace it lives in — distinct from
 * `useList()`, which limits the Collections tab to the user's
 * personal slice.
 *
 * Group-rooted Collections are returned with a `groupTitle` resolved
 * from the parent Group in the same cross-layer cache, so consumers
 * can attribute the Collection to its workspace ("My Workspace") in
 * the UI without a second fetch.
 */
function useListAllWritable() {
    const { data, isLoading } = useLiveQuery((q) => q.from({ c: collectionCollection }), []);
    const raw = (data ?? []);
    const items = useMemo(() => {
        // Index Group titles (layer 0, non-Primary) so each Group-rooted
        // Collection can attribute to its parent without an O(N) scan.
        const groupTitleById = new Map();
        for (const c of raw) {
            const layer = c.layerIndex ?? COLLECTION_LAYER.collection;
            if (layer !== COLLECTION_LAYER.group)
                continue;
            if (c.type === "primary")
                continue;
            groupTitleById.set(c.id, c.title ?? "Untitled");
        }
        const layerOne = raw.filter((c) => {
            const layer = c.layerIndex ?? COLLECTION_LAYER.collection;
            return layer === COLLECTION_LAYER.collection && c.type !== "primary";
        });
        const enriched = layerOne.map((collection) => {
            const parentId = collection.parentCollectionId;
            const groupTitle = parentId !== undefined ? groupTitleById.get(parentId) : undefined;
            return { collection, groupTitle };
        });
        return enriched.sort((a, b) => {
            const aKey = a.collection.lastAddedAt ?? a.collection.createdAt;
            const bKey = b.collection.lastAddedAt ?? b.collection.createdAt;
            if (aKey === bKey)
                return b.collection.createdAt.localeCompare(a.collection.createdAt);
            return bKey.localeCompare(aKey);
        });
    }, [raw]);
    return { items, isLoading };
}
/**
 * Cross-layer lookup by id. Reads from the unfiltered
 * `collectionCollection` cache so callers can resolve any Collection
 * regardless of taxonomic class — including Primaries (layer 0,
 * filtered out of `useList`) and Groups (layer 0). Returns undefined
 * when the id isn't in the cache (e.g. soft-deleted or the user lost
 * effective access).
 */
function useFindById(collectionId) {
    const { data } = useLiveQuery((q) => q.from({ c: collectionCollection }), []);
    return useMemo(() => {
        if (collectionId === undefined)
            return undefined;
        const raw = (data ?? []);
        return raw.find((c) => c.id === collectionId);
    }, [data, collectionId]);
}
/**
 * Flat read of every row in `collectionCollection`, regardless of
 * type / layer / Primary status. Powers URL-grammar lookups (e.g.
 * matching a `/{name}/{slug}` pair against the local cache before
 * falling back to the public resolver) that need to see ALL
 * collections — including parent Groups whose slugs anchor their
 * children's public URLs, and Primaries whose owner_id anchors
 * standalone-collection URLs.
 *
 * Distinct from `useList` (Personal Collections only) and
 * `useListAllWritable` (writable-only enrichment) — those filter the
 * underlying cache for their specific UI surfaces; this returns the
 * raw cache.
 */
function useListAllForSlugLookup() {
    const { data } = useLiveQuery((q) => q.from({ c: collectionCollection }), []);
    return useMemo(() => ({ collections: (data ?? []) }), [data]);
}
/**
 * List the user's Groups (collections with `layerIndex = 0`). Filters
 * the cross-layer `collectionCollection` live cache — the bootstrap
 * aggregator already returns every layer, so there's no need to run
 * a second `/v1/collection/list?layerIndex=0` round-trip on cold
 * load. `<AppNav>` mounts on every authenticated route and calls
 * this hook, so the extra request was firing on every fresh tab
 * even though the data was already in `collectionCollection` from
 * bootstrap.
 *
 * The Primary group (`type === "primary"`) is filtered out — it's a
 * non-shareable special case of Group that hosts each user's private
 * Collections. Surfacing it in the Groups switcher / "Add to group"
 * picker would let the user "switch into" or "move into" their own
 * Primary, which is meaningless (private Collections already live
 * there by default) and conflates the Primary container with the
 * shared-Group concept the switcher exists to expose.
 */
function useListGroups() {
    const { data, isLoading } = useLiveQuery((q) => q.from({ c: collectionCollection }), []);
    const raw = (data ?? []);
    const groups = useMemo(() => {
        return raw
            .filter((c) => (c.layerIndex ?? COLLECTION_LAYER.collection) === COLLECTION_LAYER.group &&
            c.type !== "primary")
            .sort((a, b) => {
            const aKey = a.lastAddedAt ?? a.createdAt;
            const bKey = b.lastAddedAt ?? b.createdAt;
            if (aKey === bKey)
                return b.createdAt.localeCompare(a.createdAt);
            return bKey.localeCompare(aKey);
        });
    }, [raw]);
    return { groups, isLoading };
}
/**
 * Resolve the current user's Primary Group id from the cross-layer
 * `collectionCollection` cache. Returns `undefined` while the cache
 * is still warming (e.g. before bootstrap lands), so callers should
 * gate any "create Stack under Primary" affordance on a non-undefined
 * value.
 *
 * Primary is auto-created by the server's `ensurePrimary` on first
 * use, so once any authenticated request has landed for this account
 * the id is present in cache.
 *
 * Used by the homepage Stream's drag-to-create-stack gesture: dropping
 * a block on another block while no explicit Collection parent is in
 * scope routes the create to the user's Primary Group, parenting the
 * Stack one layer below the Group instead of requiring a stand-in
 * layer-1 Collection.
 */
function usePrimaryGroupId() {
    // Narrow the live query to just the Primary row so the hook doesn't
    // re-run on every mutation to any other collection (new Stack, rename,
    // etc.). The Primary is auto-created once per account and never
    // changes id afterwards, so this resolves to a stable single-row
    // subscription after bootstrap.
    const { data } = useLiveQuery((q) => q
        .from({ c: collectionCollection })
        .where(({ c }) => eq(c.type, "primary")), []);
    const raw = (data ?? []);
    return raw[0]?.id;
}
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
async function create(input) {
    try {
        const result = await apiCreateCollection(input);
        await refreshCollections();
        return result.collection.id;
    }
    catch (e) {
        logger.warn("Failed to create collection", { error: e instanceof Error ? e.message : String(e) });
        throw e;
    }
}
async function createGroup(input) {
    try {
        const result = await apiCreateGroup(input);
        // `refreshCollections` writes every layer back into
        // `collectionCollection`, which is the same cache the Groups
        // switcher reads from now (see `useListGroups`). No separate
        // groups-only refresh is needed.
        await refreshCollections();
        return result.collection.id;
    }
    catch (e) {
        logger.warn("Failed to create group", {
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Optimistic-first Stack create.
 *
 * Writes a placeholder row to BOTH caches that drive the parent's
 * children grid (`collectionCollection` cross-layer cache +
 * `app.collectionChildren` tanstack-query cache) BEFORE the server
 * confirms, so the new Stack tile pops into the parent's view in the
 * same React commit the user clicked "Create stack". The server then
 * creates the row with the placeholder's id (forwarded as
 * `clientId`), so the eventual response matches our cache byte-for-byte
 * — no React re-key flicker, no tile swap.
 *
 * Returns the (already-known) Stack id immediately on server success.
 * The id is stable from T+0: callers can chain
 * `moveBlocksBetweenParentChild` against the resolved id and the
 * blocks land in the same Stack tile they saw appear instantly.
 *
 * On server failure we roll back BOTH cache writes and rethrow, so the
 * orphan placeholder doesn't linger.
 *
 * Works against both Collection (layer 1) and Group (layer 0) parents
 * — `apiCreateStack` always passes `childLayerIndex: 2` so the server
 * creates a Stack regardless of the parent's layer. Group parents
 * power the "stack things directly from Stream / Group view" gesture
 * (homepage Stream targets the user's Primary Group); Collection
 * parents power the in-Collection multiselect "Create stack" and
 * drag-to-create-stack flows.
 */
async function createStack(parentCollectionId, input) {
    // Caller-supplied id (e.g. from `generateId("collection")` on the
    // client) lets the caller pre-allocate the Stack id BEFORE this
    // function awaits its server response. `createStackFromBlocks`
    // relies on this so the companion `moveBlocksBetweenParentChild`
    // call can fire its optimistic writes against a known-good Stack
    // id at T+0, without waiting ~150–300ms for the create to settle.
    const placeholderId = input.clientId ?? generateId("collection");
    const now = new Date().toISOString();
    // Inherit the parent's ownerId for the brief placeholder window. The
    // server-canonical row overwrites this in ~150ms, so even a stale
    // "pending" sentinel would self-heal — but borrowing the parent's
    // owner keeps any read that hits the cache mid-flight (e.g. a
    // permission check) from short-circuiting on a non-account id.
    const parent = collectionCollection.get(parentCollectionId);
    const placeholder = {
        id: placeholderId,
        ownerId: parent?.ownerId ?? "pending",
        type: "private",
        title: input.title,
        summary: input.summary,
        metadata: {},
        createdAt: now,
        layerIndex: COLLECTION_LAYER.stack,
        parentCollectionId,
        blockCount: 0,
    };
    // 1. Cross-layer cache → drives `useFindById` consumers (slug-aware
    //    URL helpers, Stack-parent lookups, the unfurl header on open).
    collectionCollection.utils.writeInsert(placeholder);
    // 2. Parent-children tanstack cache → drives the actual grid that
    //    renders the new tile. `setQueriesData` with just the prefix
    //    matches every parent's cached children list — we know the
    //    parentId, but using the prefix keeps the rollback path
    //    symmetrical.
    queryClient.setQueriesData({ queryKey: ["app.collectionChildren", parentCollectionId] }, (old) => [...(old ?? []), placeholder]);
    try {
        // Forward the placeholder id as `clientId` so the server creates
        // the row with the SAME id our caches already hold. The response's
        // `collection.id` will equal `placeholderId`.
        const result = await apiCreateStack(parentCollectionId, {
            ...input,
            clientId: placeholderId,
        });
        // Canonicalize over the placeholder. Same id → React reuses the
        // same key, no remount, no flicker.
        collectionCollection.utils.writeUpsert(result.collection);
        queryClient.setQueriesData({ queryKey: ["app.collectionChildren", parentCollectionId] }, (old) => (old ?? []).map((c) => (c.id === placeholderId ? result.collection : c)));
        // Group-children cache: parent might also live in a Group switcher.
        void queryClient.invalidateQueries({ queryKey: ["app.groupChildren", parentCollectionId] });
        return result.collection.id;
    }
    catch (e) {
        // Roll back BOTH writes so the failed-create placeholder doesn't
        // linger in either cache.
        collectionCollection.utils.writeDelete(placeholderId);
        queryClient.setQueriesData({ queryKey: ["app.collectionChildren", parentCollectionId] }, (old) => (old ?? []).filter((c) => c.id !== placeholderId));
        logger.warn("Failed to create stack", {
            parentCollectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Create a child Collection under a parent Group (layer-0). The server
 * derives the new collection's layer from the parent (`parent + 1`),
 * so this yields a layer-1 Collection that lives inside the Group.
 *
 * Invalidates both the cross-layer cache (so the new Collection is
 * available to id-based lookups) and the Group's children cache (so
 * the new Collection shows up in the Group view immediately).
 */
async function createChildCollection(parentGroupId, input) {
    try {
        const result = await apiCreateChildCollection(parentGroupId, input);
        await refreshCollections();
        void queryClient.invalidateQueries({
            queryKey: ["app.groupChildren", parentGroupId],
        });
        void queryClient.invalidateQueries({
            queryKey: ["app.collectionChildren", parentGroupId],
        });
        return result.collection.id;
    }
    catch (e) {
        logger.warn("Failed to create child collection under group", {
            parentGroupId,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Optimistic-first title rename.
 *
 * Patches every cache that holds a copy of this collection so the new
 * title is visible in the same React commit:
 *
 *   - `collectionCollection` (cross-layer cache) — covers `useFindById`
 *     consumers like Stack-parent lookups, slug-aware URL helpers, and
 *     the `<StackFrame>` header on open.
 *   - `app.collectionChildren` / `app.groupChildren` tanstack-query
 *     caches — cover the grids that Stream renders from (the parent
 *     Collection's children list, where Stack tiles live).
 *
 * On server failure we roll back BOTH caches to the captured snapshot
 * and re-throw so the caller can surface the error message verbatim.
 * On success we run a `refreshCollections()` so any server-side
 * normalization (whitespace trim, future i18n) wins over the
 * optimistic value without a visible flicker — the optimistic write
 * matches the canonical value byte-for-byte in the common case, so
 * the refresh is a no-op render-wise.
 */
async function updateTitle(collectionId, title) {
    const previous = collectionCollection.get(collectionId);
    // 1. Cross-layer cache → drives `useFindById` everywhere.
    if (previous !== undefined) {
        collectionCollection.utils.writeUpsert({ ...previous, title });
    }
    // 2. Parent-children tanstack caches → drives the Stream grid that
    //    actually renders the Stack tile. `setQueriesData` with just the
    //    prefix matches EVERY parent's cached children array, so we don't
    //    have to know which parent the rename target lives under.
    const patchCache = (prefix) => {
        queryClient.setQueriesData({ queryKey: prefix }, (old) => {
            if (old === undefined)
                return old;
            let changed = false;
            const next = old.map((c) => {
                if (c.id !== collectionId)
                    return c;
                changed = true;
                return { ...c, title };
            });
            return changed ? next : old;
        });
    };
    patchCache(["app.collectionChildren"]);
    patchCache(["app.groupChildren"]);
    try {
        await apiUpdateCollection(collectionId, { title });
    }
    catch (e) {
        // Roll back every optimistic write so the user doesn't see a ghost
        // of a failed rename. Restore the original row in the cross-layer
        // cache and swap each child-list entry back to the snapshot.
        if (previous !== undefined) {
            collectionCollection.utils.writeUpsert(previous);
            const rollbackCache = (prefix) => {
                queryClient.setQueriesData({ queryKey: prefix }, (old) => {
                    if (old === undefined)
                        return old;
                    let changed = false;
                    const next = old.map((c) => {
                        if (c.id !== collectionId)
                            return c;
                        changed = true;
                        return previous;
                    });
                    return changed ? next : old;
                });
            };
            rollbackCache(["app.collectionChildren"]);
            rollbackCache(["app.groupChildren"]);
        }
        logger.warn("Failed to update collection title", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
    // Canonicalize from the server. The optimistic write already covers
    // the common case (title is the only change); this refresh catches
    // any server-side normalization or concurrent updates from another
    // tab without forcing the caller to await it before the UI updates.
    void refreshCollections();
}
/**
 * Move blocks along a legal parent-child edge in the
 * Group/Collection/Stack hierarchy, in either direction. Server-side
 * enforces the parent-child guardrail and the legal-pair restriction
 * (see `LEGAL_PARENT_CHILD_LAYER_PAIRS`).
 *
 * Optimistic-first: the affected blocks move from the source scope's
 * block-index to the target scope's in the same React commit, AND
 * each block envelope's `collectionId` is patched in `blockStore` so
 * consumers that read `block.collectionId` directly (e.g. the
 * homepage Stream's synthetic-stack grouping) pick up the move
 * instantly without waiting for a refetch.
 */
async function moveBlocksBetweenParentChild(blockIds, sourceCollectionId, targetCollectionId, options) {
    // Snapshot for rollback on failure.
    const previousSortKeys = new Map();
    // Envelope-level `collectionId` snapshot for rollback. The optimistic
    // patch below rewrites each block's `block.collectionId` to the
    // target so downstream consumers (e.g. the homepage Stream's
    // synthetic-stack grouping in `mergeSameCollectionBlocks`) pick up
    // the move INSTANTLY without waiting for a refetch. On server
    // failure we replay the saved previous values via `blockStore.upsert`.
    const previousEnvelopeCollectionIds = new Map();
    const now = new Date().toISOString();
    for (const blockId of blockIds) {
        const env = blockStore.get(blockId);
        // `surfaceAt` is required + canonical (NOT NULL on the server,
        // homepage timeline orders by it); fall back to `now` only when
        // the envelope isn't cached yet — e.g. moves issued from the
        // multiselect bar without first opening the source tab.
        const sortKey = env?.block.surfaceAt ?? now;
        // Find the existing source row's sortKey so we can replay it on
        // rollback. Falls through to `now` if the source scope hadn't
        // loaded yet (e.g. moves issued from the multiselect bar without
        // first opening the source tab).
        for (const row of collectionBlocksCollection.values()) {
            const r = row;
            if (r.scopeId === sourceCollectionId && r.blockId === blockId) {
                previousSortKeys.set(blockId, r.sortKey);
                break;
            }
        }
        removeBlock(sourceCollectionId, blockId);
        addBlock(targetCollectionId, blockId, sortKey);
        // Optimistic envelope patch. The synthetic-stack grouping in
        // `mergeSameCollectionBlocks` reads `block.collectionId` to bucket
        // blocks; without this write the grouping wouldn't notice the
        // move until the next `/v1/block/list` refetch landed (could be
        // seconds). With it, the dragged blocks "land inside" the new
        // Stack tile in the same React commit the user dropped them.
        if (env !== undefined) {
            previousEnvelopeCollectionIds.set(blockId, env.block.collectionId);
            blockStore.upsert({
                ...env,
                block: { ...env.block, collectionId: targetCollectionId },
            });
        }
    }
    // Optimistically patch the `stream.stackChildren` query caches for
    // both endpoints. The unfurl view reads its block list out of those
    // caches (NOT `collectionBlocksCollection`), so without these
    // writes a block moved out of an open Stack would stay visible
    // inside the unfurl until the next refetch.
    //
    // The patch is symmetric:
    //   - source endpoint: drop the moved blocks from the cached blocks
    //     list and decrement `count`.
    //   - target endpoint: insert the moved blocks (resolved from
    //     `blockStore` so each carries its real envelope content) at the
    //     position the server would sort them — `surfaceAt`-descending
    //     — and bump `count`.
    // Both writes are no-ops when no entry exists in the cache (the
    // endpoint isn't a Stack, or the user hasn't opened that Stack
    // yet), so the helper is safe to fire for every move regardless of
    // direction. The previous data is captured per-key for rollback if
    // the server rejects.
    const previousSourceStackData = queryClient.getQueryData([
        "stream.stackChildren",
        sourceCollectionId,
    ]);
    const previousTargetStackData = queryClient.getQueryData([
        "stream.stackChildren",
        targetCollectionId,
    ]);
    const movedIdSet = new Set(blockIds);
    if (previousSourceStackData !== undefined) {
        const remaining = previousSourceStackData.blocks.filter((env) => movedIdSet.has(env.block.id) === false);
        // Decrement by the FULL move size, not just `blocks.length -
        // remaining.length`. The cached `blocks` array is a paginated
        // page (capped at `STACK_DETAIL_PAGE_SIZE`); some moved blocks
        // may sit beyond the page and still be part of the stack's true
        // total. The invalidation after server success replaces this
        // approximation with the canonical count either way.
        queryClient.setQueryData(["stream.stackChildren", sourceCollectionId], {
            ...previousSourceStackData,
            blocks: remaining,
            count: Math.max(0, previousSourceStackData.count - blockIds.length),
        });
    }
    if (previousTargetStackData !== undefined) {
        const existingIds = new Set(previousTargetStackData.blocks.map((env) => env.block.id));
        const toAdd = [];
        let newlyCountedIds = 0;
        for (const blockId of blockIds) {
            if (existingIds.has(blockId))
                continue;
            newlyCountedIds += 1;
            const env = blockStore.get(blockId);
            // A missing envelope is rare (the block was just visible in the
            // source) but if it happens we still bump `count` so the total
            // reflects the move — the next refetch fills in the envelope
            // body. Only the cached `blocks` page misses out, not the count.
            if (env !== undefined)
                toAdd.push(env);
        }
        if (newlyCountedIds > 0) {
            const merged = toAdd.length === 0
                ? previousTargetStackData.blocks
                : [...toAdd, ...previousTargetStackData.blocks].sort((a, b) => b.block.surfaceAt.localeCompare(a.block.surfaceAt));
            queryClient.setQueryData(["stream.stackChildren", targetCollectionId], {
                ...previousTargetStackData,
                blocks: merged,
                count: previousTargetStackData.count + newlyCountedIds,
            });
        }
    }
    // Optimistically bump the source/target `blockCount` (and the
    // target's `lastAddedAt`) in BOTH caches that drive the parent
    // grid's tile pill — `collectionCollection` (used by id-based
    // lookups + the `<StackBlockView>` count chip via
    // `useCollectionCardPreviews`) AND the `app.collectionChildren`
    // tanstack-query cache (the array `<Stream>` actually renders
    // from). Without these writes the Stack tile's "N items" pill
    // stays at the server-canonical count from the last `/children`
    // fetch until the post-success invalidation refetch lands, which
    // can be 100–300ms after the user drops the block.
    //
    // Both writes are no-ops when the row / list isn't in cache (e.g.
    // moves issued from a surface where `app.collectionChildren`
    // hasn't been read yet). Snapshots are captured per-cache for the
    // rollback path; the post-success `refreshCollections()` +
    // `invalidateQueries` writes the canonical count over the
    // optimistic value, so any small mismatch (e.g. server-side
    // soft-deletes filtered into the count after the move) is
    // self-correcting.
    const previousCrossLayerByCollection = new Map();
    const previousChildrenLists = [];
    function bumpBlockCount(collectionId, delta) {
        if (delta === 0)
            return;
        const existing = collectionCollection.get(collectionId);
        if (previousCrossLayerByCollection.has(collectionId) === false) {
            previousCrossLayerByCollection.set(collectionId, existing);
        }
        if (existing !== undefined) {
            const nextCount = Math.max(0, (existing.blockCount ?? 0) + delta);
            const nextLastAddedAt = delta > 0 ? now : existing.lastAddedAt;
            collectionCollection.utils.writeUpsert({
                ...existing,
                blockCount: nextCount,
                lastAddedAt: nextLastAddedAt,
            });
        }
        const patchList = (prefix) => {
            queryClient.setQueriesData({ queryKey: prefix }, (old) => {
                if (old === undefined)
                    return old;
                let changed = false;
                const next = old.map((row) => {
                    if (row.id !== collectionId)
                        return row;
                    changed = true;
                    previousChildrenLists.push({ prefix, collectionId, list: old });
                    const nextCount = Math.max(0, (row.blockCount ?? 0) + delta);
                    const nextLastAddedAt = delta > 0 ? now : row.lastAddedAt;
                    return { ...row, blockCount: nextCount, lastAddedAt: nextLastAddedAt };
                });
                return changed ? next : old;
            });
        };
        patchList(["app.collectionChildren"]);
        patchList(["app.groupChildren"]);
    }
    bumpBlockCount(sourceCollectionId, -blockIds.length);
    bumpBlockCount(targetCollectionId, blockIds.length);
    try {
        // Optional gate — used by `createStackFromBlocks` to hold the
        // server move until the companion `createStack` has confirmed
        // the target Stack id (otherwise this POST would 404 against an
        // id the server hasn't created yet). The optimistic writes above
        // have already fired by the time we reach this await, so the UI
        // is instant regardless of how long `awaitBefore` takes to settle.
        if (options?.awaitBefore !== undefined) {
            await options.awaitBefore;
        }
        const result = await apiMoveBlocksBetweenParentChild(blockIds, targetCollectionId);
        void refreshCollections();
        // Children list (Stack cards inside a Collection's grid) is still
        // a tanstack-query — keep the targeted invalidate so
        // newly-promoted Stacks show up. Block envelope content is now
        // driven entirely by `collectionStore.useBlocks`, so no
        // `app.collectionDetails` / `collectionCardPreview` invalidate
        // needed.
        void queryClient.invalidateQueries({
            queryKey: ["app.collectionChildren", sourceCollectionId],
        });
        void queryClient.invalidateQueries({
            queryKey: ["app.collectionChildren", targetCollectionId],
        });
        // Refresh the stack-children caches against the canonical server
        // response so any normalization (sort tie-breakers, server-side
        // sortKey adjustments) wins over the optimistic merge above.
        // Invalidation is a no-op for keys that have no entry, so it's
        // safe to fire on the parent-Collection side too.
        void queryClient.invalidateQueries({
            queryKey: ["stream.stackChildren", sourceCollectionId],
        });
        void queryClient.invalidateQueries({
            queryKey: ["stream.stackChildren", targetCollectionId],
        });
        return result;
    }
    catch (e) {
        // Roll back the optimistic block-index moves.
        for (const blockId of blockIds) {
            removeBlock(targetCollectionId, blockId);
            const previousSortKey = previousSortKeys.get(blockId) ?? now;
            addBlock(sourceCollectionId, blockId, previousSortKey);
            // Restore the envelope's `collectionId` so the synthetic-stack
            // grouping (and any other `block.collectionId` consumer) sees
            // the pre-move state again. Only restore when we captured a
            // previous value — a cache miss at write time means there's
            // nothing meaningful to restore.
            const previousCollectionId = previousEnvelopeCollectionIds.get(blockId);
            if (previousCollectionId !== undefined) {
                const current = blockStore.get(blockId);
                if (current !== undefined) {
                    blockStore.upsert({
                        ...current,
                        block: { ...current.block, collectionId: previousCollectionId },
                    });
                }
            }
        }
        // Roll back the optimistic stack-children patches. Restoring
        // `undefined` removes any entry we wrote into a key that started
        // empty (e.g. target stack the user hadn't opened yet); restoring
        // a snapshot replays the pre-move state exactly.
        if (previousSourceStackData !== undefined) {
            queryClient.setQueryData(["stream.stackChildren", sourceCollectionId], previousSourceStackData);
        }
        if (previousTargetStackData !== undefined) {
            queryClient.setQueryData(["stream.stackChildren", targetCollectionId], previousTargetStackData);
        }
        // Roll back the optimistic blockCount / lastAddedAt patches on
        // both endpoints. `previousCrossLayerByCollection` holds the
        // pre-bump row (or `undefined` if the row wasn't cached); writing
        // `undefined` back via `writeDelete` would evict a row that was
        // legitimately there for another reason, so we restore by upsert
        // only when we captured a real previous row.
        //
        // `previousChildrenLists` captures the entire list snapshot every
        // time a `bumpBlockCount` call mutated a list. Each entry stores
        // the snapshot AS IT WAS just before that bump fired, keyed by
        // `(prefix, collectionId)`. We dedup on FIRST occurrence so a
        // hypothetical double-bump on the same row would restore from the
        // truly pre-move snapshot rather than a partially-mutated one;
        // today's call shape only bumps each (prefix, collectionId) at
        // most once so the dedup is defensive.
        for (const [collectionId, previous] of previousCrossLayerByCollection) {
            if (previous !== undefined) {
                collectionCollection.utils.writeUpsert(previous);
            }
        }
        const seenListKeys = new Set();
        for (const { prefix, collectionId, list } of previousChildrenLists) {
            const key = `${prefix.join("/")}\0${collectionId}`;
            if (seenListKeys.has(key))
                continue;
            seenListKeys.add(key);
            queryClient.setQueriesData({ queryKey: prefix }, (current) => {
                if (current === undefined)
                    return current;
                // Only restore the row we touched — the rest of the array
                // may have been mutated by concurrent writes between the
                // bump and the rollback, and clobbering it wholesale would
                // undo those.
                if (list === undefined)
                    return current;
                const previousRow = list.find((row) => row.id === collectionId);
                if (previousRow === undefined)
                    return current;
                let changed = false;
                const next = current.map((row) => {
                    if (row.id !== collectionId)
                        return row;
                    changed = true;
                    return previousRow;
                });
                return changed ? next : current;
            });
        }
        logger.error("Failed to move blocks between parent and child", {
            sourceCollectionId,
            targetCollectionId,
            blockCount: blockIds.length,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Copy a set of blocks into a collection — the path used by the
 * "Add to collection" popover, drag-drop onto a collection, smart-
 * collection save, and the omnibar's multi-collection fan-out.
 *
 * Optimistic-first: writes block-index rows for every (collectionId,
 * blockId) pair before the server roundtrip, so any open
 * collection-detail tab / card-preview stack reflects the new card in
 * the same React commit as the popover close. On server failure the
 * affected rows are rolled back.
 *
 * Sort key per row: the cached envelope's `surfaceAt`, falling back
 * to "now" when the envelope hasn't been seen yet (rare — the block
 * was just selected from a view that loaded it).
 */
async function copyBlocks(collectionId, blockIds) {
    // Optimistic block-index writes. Each card appears in the destination
    // collection's grid + card-preview stack instantly via TanStack DB's
    // live query.
    const now = new Date().toISOString();
    for (const blockId of blockIds) {
        const env = blockStore.get(blockId);
        const sortKey = env?.block.surfaceAt ?? now;
        addBlock(collectionId, blockId, sortKey);
    }
    try {
        const result = await apiCopyBlocks(collectionId, blockIds);
        // Refresh the collection metadata (blockCount, lastAddedAt) so
        // collection cards on the /collections grid update their badge.
        void refreshCollections();
        return result.copiedCount;
    }
    catch (e) {
        // Roll back the optimistic rows so the user doesn't see ghosts of
        // a failed copy.
        for (const blockId of blockIds) {
            removeBlock(collectionId, blockId);
        }
        logger.error("Failed to copy blocks to collection", {
            collectionId,
            blockCount: blockIds.length,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Move one or more layer-1 Collections under a Group. The server
 * enforces every guardrail (admin on both endpoints, layer cascade,
 * cycle / orphan-grant); this wrapper just fans out across the
 * selected collection IDs and aggregates results for the caller.
 *
 * Returns `{ movedCount, failures }`. On any failure it does NOT
 * roll back the moves that succeeded — the user keeps the partial
 * progress, and the action bar surfaces a toast describing the
 * affected ids.
 *
 * Invalidates: the cross-layer cache (so the moved Collections drop
 * out of `useList()` since they now have a `parentCollectionId`),
 * the destination group's children cache (so the new children show
 * up in the Group view immediately), and the groups list (so member
 * counts / freshness ordering catch up).
 */
/**
 * Detach a Collection from the caller's Primary into the standalone
 * state. Idempotent: a no-op (returns silently) when the Collection is
 * already standalone.
 *
 * Used by the share-without-Group flow: the modal calls this once
 * before adding the first member to a Collection that was previously
 * Primary-rooted.
 */
async function detachFromPrimary(collectionId) {
    try {
        await apiDetachCollectionFromPrimary(collectionId);
        await refreshCollections();
    }
    catch (e) {
        logger.warn("Failed to detach collection from primary", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
/**
 * Optimistic-first soft delete for a Collection (standalone, Group-rooted,
 * or Stack). Used by the multiselect "Delete" affordance on stack
 * tiles. Layer-0 Groups should go through a dedicated `deleteGroup`
 * helper if/when added — those have a slightly different permission
 * model and the route lives on `/v1/group/:id`.
 *
 * The tile vanishes in the same React commit by removing the row from
 * every cache that drives the parent's grid:
 *
 *   - `collectionCollection` — cross-layer cache; backs `useFindById`
 *     and the `useList()` surface filter.
 *   - `app.collectionChildren` / `app.groupChildren` tanstack-query
 *     caches under the parent's id — the actual lists the Stream
 *     reads. We strip the entry by id, leaving the rest of the
 *     children intact.
 *   - `stream.stackChildren` under THIS collection's id — the unfurl's
 *     full member list. Dropped wholesale so a re-open after delete
 *     doesn't flash stale members.
 *   - `collectionBlocksCollection` rows scoped to this id —
 *     per-collection block-index rows that drive `useBlocks(scopeId)`.
 *
 * On server failure we roll back the cross-layer cache and the
 * parent-children list (the two surfaces with a visible "where did
 * the stack go?" effect). The `stream.stackChildren` /
 * `collectionBlocksCollection` invalidations stay dropped — those
 * are read-through caches that will re-populate on next mount, no
 * rollback drama needed.
 */
async function softDelete(collectionId) {
    const previous = collectionCollection.get(collectionId);
    const parentId = previous?.parentCollectionId;
    // Snapshot the parent-children list entries up front so we can
    // restore them byte-for-byte on rollback. `setQueriesData` with
    // just the prefix matches every parent's cached children array, so
    // the snapshot is keyed on `(prefix, parentId)` rather than the
    // full key (which we know but mirror via prefix for symmetry with
    // other writers in this file).
    const previousChildrenLists = [];
    if (parentId !== undefined) {
        for (const prefix of [
            ["app.collectionChildren"],
            ["app.groupChildren"],
        ]) {
            const list = queryClient.getQueryData([
                ...prefix,
                parentId,
            ]);
            previousChildrenLists.push({ prefix, list });
        }
    }
    collectionCollection.utils.writeDelete(collectionId);
    if (parentId !== undefined) {
        const stripDeleted = (old) => {
            if (old === undefined)
                return old;
            const next = old.filter((c) => c.id !== collectionId);
            return next.length === old.length ? old : next;
        };
        queryClient.setQueriesData({ queryKey: ["app.collectionChildren", parentId] }, stripDeleted);
        queryClient.setQueriesData({ queryKey: ["app.groupChildren", parentId] }, stripDeleted);
    }
    queryClient.removeQueries({
        queryKey: ["stream.stackChildren", collectionId],
    });
    removeAllBlocksFromScope(collectionId);
    try {
        await apiDeleteCollection(collectionId);
    }
    catch (e) {
        if (previous !== undefined) {
            collectionCollection.utils.writeUpsert(previous);
        }
        if (parentId !== undefined) {
            for (const { prefix, list } of previousChildrenLists) {
                queryClient.setQueryData([...prefix, parentId], list);
            }
        }
        logger.warn("Failed to delete collection", {
            collectionId,
            error: e instanceof Error ? e.message : String(e),
        });
        throw e;
    }
}
async function moveCollectionsToGroup(collectionIds, targetGroupId) {
    if (collectionIds.length === 0) {
        return { movedCount: 0, failures: [], warnings: [] };
    }
    const results = await Promise.allSettled(collectionIds.map((id) => apiSetCollectionParent(id, targetGroupId)));
    const failures = [];
    // Aggregate warnings from each successful move. The server emits
    // them when the move cleared direct CollectionMember rows; the UI
    // surfaces the union as a toast so the user sees a single concise
    // notice rather than one per moved Collection. Dedup with a Set
    // since most batches will produce the same "you were removed as
    // direct owner" warning across several Collections.
    const warningSet = new Set();
    let movedCount = 0;
    results.forEach((r, i) => {
        if (r.status === "fulfilled") {
            movedCount += 1;
            for (const w of r.value.warnings)
                warningSet.add(w);
        }
        else {
            const err = r.reason;
            failures.push({
                collectionId: collectionIds[i],
                error: err instanceof Error ? err.message : String(err),
            });
        }
    });
    if (failures.length > 0) {
        logger.warn("Some collection-to-group moves failed", {
            targetGroupId,
            failures,
        });
    }
    if (movedCount > 0) {
        // `refreshCollections` rewrites every layer of
        // `collectionCollection`, which is the same cache `useListGroups`
        // now reads from — one round-trip covers both the Collections
        // tab and the Groups switcher.
        await refreshCollections();
        void queryClient.invalidateQueries({
            queryKey: ["app.groupChildren", targetGroupId],
        });
        void queryClient.invalidateQueries({
            queryKey: ["app.collectionChildren", targetGroupId],
        });
    }
    return { movedCount, failures, warnings: Array.from(warningSet) };
}
// ---------------------------------------------------------------------------
// Per-scope block index (rows in `collectionBlocksCollection`,
// envelopes in `blockStore`).
// ---------------------------------------------------------------------------
const DEFAULT_BLOCK_PAGE_SIZE = 50;
const _scopeState = new Map();
const _scopeListeners = new Set();
function _ensureScopeState(scopeId) {
    let s = _scopeState.get(scopeId);
    if (s === undefined) {
        s = { offset: 0, hasMore: true, loading: false, initialLoading: false, loadedAt: undefined };
        _scopeState.set(scopeId, s);
    }
    return s;
}
function _notifyScope() {
    for (const fn of _scopeListeners)
        fn();
}
/**
 * Reactive list of `(blockId, sortKey)` rows for a scope. Cards
 * resolve their own envelopes via `blockStore.useById` (Design B), so
 * this hook only needs to surface ids + sort metadata.
 *
 * `scopeId` can be any string the caller wants to scope by:
 * collection id, stack id, group id, or smart-collection transient
 * id. Different scopes never collide because rows are keyed by
 * `(scopeId, blockId)`.
 */
function useBlocks(scopeId, options = {}) {
    const { limit } = options;
    const [, tick] = useState(0);
    // Subscribe to per-scope state changes (loading flags, hasMore).
    useEffect(() => {
        const fn = () => tick((n) => n + 1);
        _scopeListeners.add(fn);
        return () => {
            _scopeListeners.delete(fn);
        };
    }, []);
    const { data } = useLiveQuery((q) => {
        let query = q.from({ r: collectionBlocksCollection });
        // Empty `scopeId` -> unsatisfiable filter so the join doesn't
        // accidentally surface every scope's rows during teardown.
        query = query.where(({ r }) => eq(r.scopeId, scopeId ?? "__nope__"));
        return query.orderBy(({ r }) => r.sortKey, "desc");
    }, [scopeId]);
    const rows = useMemo(() => {
        const all = (data ?? []);
        const sliced = limit !== undefined ? all.slice(0, limit) : all;
        return sliced.map((r) => ({ blockId: r.blockId, sortKey: r.sortKey }));
    }, [data, limit]);
    const state = scopeId !== undefined ? _scopeState.get(scopeId) : undefined;
    const isLoading = state?.initialLoading ?? false;
    const isFetchingNextPage = state?.loading ?? false;
    // `hasMore` defaults to `true` until `loadBlocks` lands a short page,
    // mirroring the "infinite scroll" UX. Smart-collection scopes
    // (loaded via `loadBlocksByIds`) set `hasMore = false` directly.
    const hasMore = state?.hasMore ?? true;
    const fetchNextPage = useMemo(() => async () => {
        if (scopeId === undefined)
            return;
        await loadBlocks(scopeId);
    }, [scopeId]);
    return { rows, isLoading, isFetchingNextPage, hasMore, fetchNextPage };
}
/**
 * Fetch a page of blocks for a scope from
 * `getCollectionDetails(scopeId, ...)`. Upserts envelopes into
 * `blockStore` and writes membership rows into the index. On the
 * first call for a scope, sets `initialLoading: true` so consumers
 * can render a skeleton.
 *
 * Idempotent for concurrent calls — short-circuits if a load is
 * already in flight or if the scope is exhausted.
 */
async function loadBlocks(scopeId, options = {}) {
    const limit = options.limit ?? DEFAULT_BLOCK_PAGE_SIZE;
    const state = _ensureScopeState(scopeId);
    if (state.loading)
        return;
    if (!state.hasMore)
        return;
    const isFirst = state.loadedAt === undefined;
    if (isFirst)
        state.initialLoading = true;
    state.loading = true;
    _notifyScope();
    try {
        const offset = options.offset ?? state.offset;
        const res = await getCollectionDetails(scopeId, { limit, offset });
        const envelopes = res.blocks;
        // Upsert the collection metadata so the consumer's header / lookup
        // hits the cross-layer cache on first paint without waiting for the
        // separate `listCollections` refresh.
        if (res.collection) {
            collectionCollection.utils.writeUpsert(res.collection);
        }
        blockStore.upsertMany(envelopes);
        const now = new Date().toISOString();
        collectionBlocksCollection.utils.writeBatch(() => {
            for (const env of envelopes) {
                const blockId = env.block.id;
                const sortKey = env.block.surfaceAt;
                const row = { scopeId, blockId, sortKey, addedAt: now };
                collectionBlocksCollection.utils.writeUpsert(row);
            }
        });
        state.offset = offset + envelopes.length;
        state.hasMore = envelopes.length >= limit;
        state.loadedAt = Date.now();
    }
    catch (e) {
        logger.warn("Failed to load blocks for scope", {
            scopeId,
            error: e instanceof Error ? e.message : String(e),
        });
    }
    finally {
        state.loading = false;
        state.initialLoading = false;
        _notifyScope();
    }
}
/**
 * Hydrate a scope from a known list of block ids (smart collection
 * use case). Fetches each envelope into `blockStore` (skipping ids
 * already in the cache) and writes index rows. Sets
 * `hasMore: false` because there's no pagination — the id list is
 * the entire scope.
 */
async function loadBlocksByIds(scopeId, blockIds) {
    if (blockIds.length === 0)
        return;
    const state = _ensureScopeState(scopeId);
    if (state.loading)
        return;
    if (state.loadedAt !== undefined)
        return;
    state.initialLoading = true;
    state.loading = true;
    _notifyScope();
    try {
        // Fetch envelopes the cache doesn't have yet; reuse what's already
        // in `blockStore` for the rest. The smart-collection caller writes
        // membership rows for ALL ids, not just the freshly-fetched ones.
        const missing = [];
        const present = [];
        for (const id of blockIds) {
            const cached = blockStore.get(id);
            if (cached)
                present.push(cached);
            else
                missing.push(id);
        }
        const fetched = await Promise.all(missing.map((id) => fetchBlockEnvelope(id)));
        const allEnvelopes = [
            ...present,
            ...fetched.filter((e) => e !== undefined),
        ];
        blockStore.upsertMany(allEnvelopes);
        // Order is significant — smart collections deliver ids in rank
        // order (most relevant first). Use a synthetic descending sortKey
        // so `useBlocks(scopeId)` renders them in that order.
        const now = new Date().toISOString();
        const total = blockIds.length;
        collectionBlocksCollection.utils.writeBatch(() => {
            for (let i = 0; i < blockIds.length; i++) {
                const blockId = blockIds[i];
                // Pad to fixed width so string-sort matches numeric rank.
                const rank = String(total - i).padStart(8, "0");
                const sortKey = `rank:${rank}`;
                const row = { scopeId, blockId, sortKey, addedAt: now };
                collectionBlocksCollection.utils.writeUpsert(row);
            }
        });
        state.hasMore = false;
        state.loadedAt = Date.now();
    }
    catch (e) {
        logger.warn("Failed to load blocks by id for scope", {
            scopeId,
            blockIdsCount: blockIds.length,
            error: e instanceof Error ? e.message : String(e),
        });
    }
    finally {
        state.loading = false;
        state.initialLoading = false;
        _notifyScope();
    }
}
/**
 * Re-fetch the top page for one previously-loaded scope. Used by the
 * return-from-background path (`useStaleRefresh`) so the collection
 * the user is sitting on picks up new blocks captured while they
 * were away.
 *
 * Deliberately silent: doesn't touch `state.loading` /
 * `state.initialLoading` (those drive user-visible spinners) and
 * doesn't notify scope listeners until the upsert lands. The
 * `useLiveQuery` over `collectionBlocksCollection` re-evaluates on
 * the row writes, so the grid updates without flashing a skeleton.
 *
 * State touched:
 *   - `loadedAt`: bumped on success so subsequent refreshes can
 *     reason about "when was this scope last refreshed".
 *   - `offset` / `hasMore`: untouched. Those are the user's
 *     pagination cursor; a top-page refresh shouldn't rewind
 *     `fetchNextPage` back to the start of the list.
 *
 * Stale rows below the refreshed top page are intentionally left in
 * place. A block removed from this collection while the user was
 * away would still appear here — fixing that would require diffing
 * the top page against existing rows and deleting the gap, but
 * that races with `fetchNextPage` (a row may be missing from the
 * top page because it's further down the list, not because it was
 * removed). For the "haven't visited in a while → show me new
 * captures at the top" case, an upsert-only refresh is correct.
 */
async function refreshScopeTopPage(scopeId) {
    // Smart-collection scopes are populated from an explicit id list
    // (`loadBlocksByIds`) produced by an earlier assistant query —
    // there's no server-paginated "top page" to refresh without re-
    // running the query, so leave the existing rows alone.
    if (scopeId.startsWith("transient:"))
        return;
    const state = _scopeState.get(scopeId);
    // Only refresh scopes that completed at least one `loadBlocks` —
    // scopes registered mid-flight (or never loaded at all) aren't
    // showing data the user needs to see freshened.
    if (!state || state.loadedAt === undefined)
        return;
    try {
        const res = await getCollectionDetails(scopeId, {
            limit: DEFAULT_BLOCK_PAGE_SIZE,
            offset: 0,
        });
        if (res.collection) {
            collectionCollection.utils.writeUpsert(res.collection);
        }
        blockStore.upsertMany(res.blocks);
        const now = new Date().toISOString();
        collectionBlocksCollection.utils.writeBatch(() => {
            for (const env of res.blocks) {
                const row = {
                    scopeId,
                    blockId: env.block.id,
                    sortKey: env.block.surfaceAt,
                    addedAt: now,
                };
                collectionBlocksCollection.utils.writeUpsert(row);
            }
        });
        state.loadedAt = Date.now();
    }
    catch (e) {
        logger.warn("Failed to refresh top page for scope", {
            scopeId,
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
/**
 * Re-fetch the top page of every previously-loaded scope in parallel.
 * Called from `useStaleRefresh` when the tab returns from a long
 * background — the active collection view (and any collections with
 * mounted preview cards) get fresh top blocks without the user
 * having to navigate away and back.
 *
 * `allSettled` so one scope's failure (e.g. a collection the user
 * lost access to while away) doesn't block the rest.
 */
async function refreshScopeTopPages() {
    const scopeIds = Array.from(_scopeState.keys());
    await Promise.allSettled(scopeIds.map(refreshScopeTopPage));
}
/**
 * Insert (or refresh) a single membership row. The caller supplies
 * the `sortKey` because the source-of-truth varies — for a fresh
 * capture it's the placeholder's `surfaceAt`; for an "add to
 * collection" of an existing block it's the cached envelope's
 * `surfaceAt`.
 */
function addBlock(scopeId, blockId, sortKey) {
    const now = new Date().toISOString();
    const row = { scopeId, blockId, sortKey, addedAt: now };
    collectionBlocksCollection.utils.writeUpsert(row);
}
function removeBlock(scopeId, blockId) {
    const key = collectionBlockRowKey({
        scopeId,
        blockId: blockId,
        sortKey: "",
        addedAt: "",
    });
    collectionBlocksCollection.utils.writeDelete(key);
}
/**
 * Wipe every membership row for a block id, across every scope.
 * Called by `blockStore.remove` so a deleted block disappears from
 * every open collection-detail / stack / group view in lockstep.
 */
function removeBlockEverywhere(blockId) {
    const matches = [];
    for (const row of collectionBlocksCollection.values()) {
        const r = row;
        if (r.blockId === blockId)
            matches.push(collectionBlockRowKey(r));
    }
    if (matches.length === 0)
        return;
    collectionBlocksCollection.utils.writeBatch(() => {
        for (const key of matches)
            collectionBlocksCollection.utils.writeDelete(key);
    });
}
/**
 * Drop every row for a scope. Used to clean up smart-collection
 * transient scopes when the user discards/saves the tab, and for
 * `invalidateBlocks` callers that want a hard refetch on next mount.
 */
function removeAllBlocksFromScope(scopeId) {
    const matches = [];
    for (const row of collectionBlocksCollection.values()) {
        const r = row;
        if (r.scopeId === scopeId)
            matches.push(collectionBlockRowKey(r));
    }
    if (matches.length > 0) {
        collectionBlocksCollection.utils.writeBatch(() => {
            for (const key of matches)
                collectionBlocksCollection.utils.writeDelete(key);
        });
    }
    _scopeState.delete(scopeId);
    _notifyScope();
}
/**
 * Update `sortKey` on every row matching `blockId`, across every
 * scope. Called by `blockStore.surface` so a bumped block reorders
 * not just in the home stream but also in every collection view it
 * appears in.
 */
function bumpBlockSortKey(blockId, sortKey) {
    const updates = [];
    for (const row of collectionBlocksCollection.values()) {
        const r = row;
        if (r.blockId !== blockId)
            continue;
        if (r.sortKey === sortKey)
            continue;
        updates.push({ ...r, sortKey });
    }
    if (updates.length === 0)
        return;
    collectionBlocksCollection.utils.writeBatch(() => {
        for (const row of updates)
            collectionBlocksCollection.utils.writeUpsert(row);
    });
}
/**
 * Drop scope state + rows so the next mount triggers a fresh
 * `loadBlocks`. Use sparingly — most code paths should rely on
 * optimistic writes propagating through `useLiveQuery` instead of a
 * full refetch.
 */
function invalidateBlocks(scopeId) {
    removeAllBlocksFromScope(scopeId);
}
// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const collectionStore = {
    useList,
    useListAllWritable,
    useListAllForSlugLookup,
    useListGroups,
    usePrimaryGroupId,
    useFindById,
    create,
    createGroup,
    createStack,
    createChildCollection,
    updateTitle,
    detachFromPrimary,
    softDelete,
    moveBlocksBetweenParentChild,
    moveCollectionsToGroup,
    copyBlocks,
    refreshCollections,
    // Per-collection block index
    useBlocks,
    loadBlocks,
    loadBlocksByIds,
    refreshScopeTopPages,
    addBlock,
    removeBlock,
    removeBlockEverywhere,
    removeAllBlocksFromScope,
    bumpBlockSortKey,
    invalidateBlocks,
};
